<!-- src/components/IngredientModal.vue -->
<template>
  <Teleport to="body">
  <div v-if="show" class="fixed inset-0 z-[300] flex items-center justify-center">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="attemptClose"></div>

    <!-- Modal -->
    <div class="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 z-10 m-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">
            {{ isEditMode ? 'Edit Ingredient' : 'Create New Ingredient' }}
          </h2>
          <p class="text-gray-600 mt-1">
            {{ isEditMode ? 'Update ingredient information' : 'Add a new ingredient to your catalog' }}
          </p>
        </div>
        <button @click="attemptClose" class="text-gray-600 hover:text-gray-800 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="submitIngredient" novalidate>
        <FormErrorSummary :errors="errorSummary" class="mb-6" />

        <div class="space-y-6">
          <!-- Name Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Ingredient Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
            </label>
            <input v-model="formData.name" type="text" placeholder="e.g., Carrots, Olive Oil, Salt"
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-[16px] sm:text-sm transition-colors"
              :class="nameDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="touchField('name')" />
            <p v-if="nameDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ nameDisplayError }}</p>
          </div>

          <!-- Type Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Type <span class="text-red-500 ml-0.5" aria-label="required">*</span>
            </label>
            <input v-model="formData.type" type="text" placeholder="e.g., Vegan, Dairy, Meat, Grain"
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-[16px] sm:text-sm transition-colors"
              :class="typeDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              @blur="touchField('type')" />
            <p v-if="typeDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ typeDisplayError }}</p>
            <p class="text-gray-500 text-sm mt-1">Specify the ingredient category or dietary classification</p>
          </div>

          <!-- Group Field -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                Group <span class="text-red-500 ml-0.5" aria-label="required">*</span>
              </label>
              <button @click="toggleGroupMode" type="button"
                class="text-sm text-green-600 hover:text-green-700 font-medium transition-colors">
                {{ isCreatingNewGroup ? 'Select existing' : 'Create new' }}
              </button>
            </div>

            <!-- Existing Group Selection -->
            <div v-if="!isCreatingNewGroup">
              <select v-model="formData.group"
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-[16px] sm:text-sm transition-colors"
                :class="groupDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                :disabled="loadingGroups"
                @blur="touchField('group')">
                <option value="">Select a group</option>
                <option v-for="group in ingredientGroups" :key="group.id" :value="group.name">
                  {{ group.name }}
                </option>
              </select>
              <p v-if="loadingGroups" class="text-gray-500 text-sm mt-1">Loading groups...</p>
            </div>

            <!-- New Group Input -->
            <div v-else>
              <input v-model="formData.group" type="text"
                placeholder="Enter new group name (e.g., Vegetables, Spices, Proteins)"
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-[16px] sm:text-sm transition-colors"
                :class="groupDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                @blur="touchField('group')" />
              <p class="text-gray-500 text-sm mt-1">This will create a new group if it doesn't exist</p>
            </div>

            <p v-if="groupDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ groupDisplayError }}</p>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button @click="attemptClose" type="button"
            class="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button type="submit" :disabled="submitting"
            class="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
            <div v-if="submitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            {{ isEditMode ? 'Update' : 'Create' }} Ingredient
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog
    :show="showUnsavedChangesDialog"
    @keep-editing="keepEditing"
    @discard="discardChanges"
  />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { CREATE_INGREDIENT, UPDATE_INGREDIENT } from '../graphql/mutations'
import { GET_INGREDIENT_GROUPS, GET_ALL_INGREDIENTS } from '../graphql/queries'
import { showError, showSuccess } from '../utils/errorHandling'
import { useFormValidation } from '../composables/useFormValidation'
import FormErrorSummary from './FormErrorSummary.vue'
import { vm } from '../utils/validationMessages'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

// Props
interface Props {
  show: boolean
  ingredient?: any
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)

// Emits
const emit = defineEmits<{
  close: []
  submitted: [ingredient: any]
}>()

// State
const formData = ref({
  name: '',
  type: '',
  group: ''
})

const originalFormData = ref({
  name: '',
  type: '',
  group: ''
})

const formErrors = ref({
  name: '',
  type: '',
  group: ''
})

const isCreatingNewGroup = ref(false)
const submitting = ref(false)
const showUnsavedChangesDialog = ref(false)
const pendingClose = ref(false)

const { submitAttempted, touch: touchValidation, displayError, attemptSubmit: attemptFormSubmit, reset: resetValidation } = useFormValidation()

const touchField = (field: string) => touchValidation(field)

const nameDisplayError = computed(() => displayError('name', formErrors.value.name))
const typeDisplayError = computed(() => displayError('type', formErrors.value.type))
const groupDisplayError = computed(() => displayError('group', formErrors.value.group))

const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (formErrors.value.name) items.push({ label: 'Ingredient Name', message: formErrors.value.name })
  if (formErrors.value.type) items.push({ label: 'Type', message: formErrors.value.type })
  if (formErrors.value.group) items.push({ label: 'Group', message: formErrors.value.group })
  return items
})

// GraphQL queries and mutations
const { result: groupsResult, loading: loadingGroups, refetch: refetchGroups } = useQuery(GET_INGREDIENT_GROUPS)
const { mutate: createIngredient } = useMutation(CREATE_INGREDIENT, {
  update: (cache, { data }) => {
    // Update the GET_ALL_INGREDIENTS cache with the new ingredient
    if (data?.createIngredient) {
      try {
        const existingData = cache.readQuery({ query: GET_ALL_INGREDIENTS })
        if (existingData) {
          cache.writeQuery({
            query: GET_ALL_INGREDIENTS,
            data: {
              getAllIngredients: [...(existingData as any).getAllIngredients || [], data.createIngredient]
            }
          })
        }
      } catch (error) {
        // The query might not be in cache yet, that's okay
        console.log('GET_ALL_INGREDIENTS not in cache yet')
      }
    }
  }
})
const { mutate: updateIngredient } = useMutation(UPDATE_INGREDIENT)

// Computed properties
const isEditMode = computed(() => !!props.ingredient)
const ingredientGroups = computed(() => groupsResult.value?.getAllIngredientGroups || [])

const hasUnsavedChanges = computed(() => {
  return formData.value.name !== originalFormData.value.name ||
         formData.value.type !== originalFormData.value.type ||
         formData.value.group !== originalFormData.value.group
})

// Methods
const resetForm = () => {
  formData.value = {
    name: '',
    type: '',
    group: ''
  }
  originalFormData.value = {
    name: '',
    type: '',
    group: ''
  }
  formErrors.value = {
    name: '',
    type: '',
    group: ''
  }
  isCreatingNewGroup.value = false
  submitting.value = false
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
  resetValidation()
}

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    pendingClose.value = true
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  resetForm()
  emit('close')
}

const keepEditing = () => {
  showUnsavedChangesDialog.value = false
  pendingClose.value = false
}

const discardChanges = () => {
  closeModal()
}

const validateForm = () => {
  formErrors.value = {
    name: '',
    type: '',
    group: ''
  }

  let isValid = true

  if (!formData.value.name.trim()) {
    formErrors.value.name = vm.ingredientName.required
    isValid = false
  }

  if (!formData.value.type.trim()) {
    formErrors.value.type = vm.ingredientType.required
    isValid = false
  }

  if (!formData.value.group.trim()) {
    formErrors.value.group = vm.ingredientGroup.required
    isValid = false
  }

  return isValid
}

const toggleGroupMode = () => {
  isCreatingNewGroup.value = !isCreatingNewGroup.value
  formData.value.group = ''
}

const submitIngredient = async () => {
  validateForm()
  if (!attemptFormSubmit({ name: formErrors.value.name, type: formErrors.value.type, group: formErrors.value.group })) return

  submitting.value = true

  try {
    let result

    if (isEditMode.value) {
      result = await updateIngredient({
        id: props.ingredient.id,
        name: formData.value.name.trim(),
        type: formData.value.type.trim(),
        group: formData.value.group.trim()
      })
      showSuccess(`Ingredient "${formData.value.name}" updated successfully!`)
    } else {
      result = await createIngredient({
        name: formData.value.name.trim(),
        type: formData.value.type.trim(),
        group: formData.value.group.trim()
      })
      showSuccess(`Ingredient "${formData.value.name}" created successfully!`)
    }

    emit('submitted', result?.data?.createIngredient || result?.data?.updateIngredient)
    closeModal()
  } catch (error) {
    showError(error, isEditMode.value ? 'update ingredient' : 'create ingredient')
  } finally {
    submitting.value = false
  }
}

// Watch for ingredient prop changes (for edit mode)
watch(() => props.ingredient, (newIngredient) => {
  if (newIngredient && typeof newIngredient === 'object') {
    const ingredientData = {
      name: newIngredient.name || '',
      type: newIngredient.type || '',
      group: newIngredient.group?.name || ''
    }
    formData.value = { ...ingredientData }
    originalFormData.value = { ...ingredientData }
    // If editing, use existing group selection mode
    isCreatingNewGroup.value = false
  }
}, { immediate: true })

// Watch for show prop changes to reset form when modal closes and refetch groups when opening
watch(() => props.show, async (show) => {
  if (show) {
    // Refetch groups when modal opens to ensure we have the latest data
    await refetchGroups()
  } else {
    resetForm()
  }
})
</script>
